import { useCallback, useEffect, useMemo } from 'react';

import { TaskPriority, TaskStatus } from '@/entities/vfo/types';
import { useNotices } from '@/features/notices/useNotices';
import {
  ApiCreateTaskDtoV2,
  ApiTaskResponseV2,
  ApiUpdateTaskDtoV2,
} from '@/shared/api/generated/data-contracts';
import { useApi } from '@/shared/api/hooks/useApi';
import { useAuthStore } from '@/shared/store/auth.store';
import { compareDates } from '@/shared/utils/compare';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';

import { useCasesStore } from '../stores/cases.store';
import { useTasksStore } from '../stores/tasks.store';

import { useCases } from './useCases';
import { useMembers } from './useMembers';

const sortTasks = (tasks: ApiTaskResponseV2[]) => {
  const statusOrder: Record<TaskStatus, number> = {
    [TaskStatus.TODO]: 0,
    [TaskStatus.IN_PROGRESS]: 1,
    [TaskStatus.DONE]: 2,
    [TaskStatus.CANCELLED]: 3,
  };

  const priorityOrder: Record<TaskPriority, number> = {
    [TaskPriority.URGENT]: 0,
    [TaskPriority.HIGH]: 1,
    [TaskPriority.MEDIUM]: 2,
    [TaskPriority.LOW]: 3,
  };

  return [...tasks].sort((a, b) => {
    const status = statusOrder[a.status] - statusOrder[b.status];
    if (status !== 0) return status;

    const priority = priorityOrder[a.priority] - priorityOrder[b.priority];
    if (priority !== 0) return priority;

    return compareDates(b.createdAt, a.createdAt);
  });
};

export const useTasks = () => {
  const { t: tf } = useTranslation('features/vfo', { keyPrefix: 'taskManager' });
  const api = useApi();
  const notices = useNotices();
  const { account } = useAuthStore();
  const { currentUser } = useMembers();
  const {
    tasks,
    tasksCreatedByMe,
    isLoading,
    setTasks,
    setMyCreatedTasks,
    addTask,
    updateTask,
    removeTask: removeTaskFromStore,
    updateTaskDocuments: updateTaskDocumentStore,
    removeTaskDocument: removeTaskDocumentFromStore,
    setLoading,
    setInitialized,
  } = useTasksStore();
  const { findCase, fetchCases } = useCases();
  const { updateCase } = useCasesStore();

  const sortedTasks = useMemo(() => sortTasks(tasks), [tasks]);
  const sortedTasksCreatedByMe = useMemo(() => sortTasks(tasksCreatedByMe), [tasksCreatedByMe]);

  const tasksAssignedToMe = useMemo(() => {
    return sortedTasks.filter((task) => task.assignee?.profileId === account?.id);
  }, [sortedTasks, account]);

  const fetchTasks = useCallback(async () => {
    setLoading(true);

    const [allTasksResponse, createdByMeTasksResponse] = await Promise.allSettled([
      api.tasksGetAll(),
      api.tasksGetAll({ filter: 'created_by_me' }),
    ]);

    if (
      allTasksResponse.status === 'rejected' ||
      createdByMeTasksResponse.status === 'rejected' ||
      !allTasksResponse.value?.data ||
      !createdByMeTasksResponse.value?.data
    ) {
      notices.error('Failed to load tasks');
      setLoading(false);
      setInitialized(true);
      return;
    }

    setTasks(allTasksResponse.value.data);
    setMyCreatedTasks(createdByMeTasksResponse.value.data);

    setLoading(false);
    setInitialized(true);
  }, [api, notices, setTasks, setMyCreatedTasks, setLoading, setInitialized]);

  const createTask = useCallback(
    async (data: ApiCreateTaskDtoV2) => {
      const response = await api.tasksCreate(data);
      if (response.error || !response.data) {
        notices.error('Failed to create task');
        return null;
      }

      if (currentUser?.profileId) {
        addTask(response.data, currentUser.profileId);
      }

      fetchCases();
      return response.data;
    },
    [api, notices, addTask],
  );

  const getCaseTasks = useCallback(
    (caseId: string) => {
      return sortedTasks.filter((task) => task.caseId === caseId);
    },
    [sortedTasks],
  );

  const getAssigneeTasks = useCallback(
    (profileId: number) => {
      return sortedTasks.filter((task) => task.assignee?.profileId === profileId);
    },
    [sortedTasks],
  );

  const updateTaskDetails = useCallback(
    async (taskId: string, data: ApiUpdateTaskDtoV2): Promise<boolean> => {
      const oldTask = findById(taskId);
      const response = await api.tasksUpdate(taskId, data);

      if (response.error || !response.data) {
        notices.error('Failed to update task');
        return false;
      }

      updateTask(taskId, response.data);
      if (data.caseId) {
        const newCaseData = findCase(data.caseId);
        const oldCaseData = findCase(oldTask?.caseId);
        updateCase(data.caseId, {
          tasks: [...(newCaseData?.tasks ?? []), response.data],
        });
        if (oldCaseData?.id) {
          updateCase(oldCaseData.id, {
            tasks: oldCaseData?.tasks.filter((item) => item.id !== oldTask?.id),
          });
        }
      }
      return true;
    },
    [api, notices, updateTask, updateCase],
  );

  const deleteTask = useCallback(
    async (taskId: string, showNotice: boolean = true): Promise<boolean> => {
      const response = await api.tasksDelete(taskId);
      if (response.error) {
        notices.error('Failed to delete task');
        return false;
      }

      if (showNotice) {
        notices.success('Task has been deleted');
      }

      removeTaskFromStore(taskId);
      return true;
    },
    [api, notices, removeTaskFromStore],
  );

  const addTaskDocuments = useCallback(
    async (taskId: string, documentIds: string[]) => {
      const response = await api.taskAddDocuments(taskId, { documentIds });

      if (response.error || !response.data?.documents) {
        notices.error('Failed to add');
        return false;
      }

      notices.success(tf(documentIds.length > 1 ? 'documentsAddedPlural' : 'documentsAdded'));
      updateTaskDocumentStore(taskId, response.data?.documents);
      return true;
    },
    [api, tf],
  );

  const removeTaskDocument = useCallback(
    async (taskId: string, documentId: string) => {
      const response = await api.taskRemoveDocument(taskId, documentId);

      if (response.error) {
        notices.error('Failed to remove');
        return false;
      }

      notices.success('Document has been deleted');
      removeTaskDocumentFromStore(taskId, documentId);
      return true;
    },
    [api],
  );

  const findById = useCallback(
    (taskId: string) => {
      return tasks.find((item) => item.id === taskId);
    },
    [tasks],
  );

  useEffect(() => {
    // get the live store state to avoid race conditions
    const { isInitialized, isLoading: isLoadingLocal } = useTasksStore.getState();
    if (!isInitialized && !isLoadingLocal) {
      fetchTasks();
    }
  }, [fetchTasks]);

  return useMemo(
    () => ({
      tasks: sortedTasks,
      tasksCreatedByMe: sortedTasksCreatedByMe,
      tasksAssignedToMe,
      isLoading,
      createTask,
      updateTask: updateTaskDetails,
      deleteTask,
      addTaskDocuments,
      removeTaskDocument,
      fetchTasks,
      getCaseTasks,
      getAssigneeTasks,
      findById,
    }),
    [
      sortedTasks,
      sortedTasksCreatedByMe,
      tasksAssignedToMe,
      isLoading,
      createTask,
      updateTaskDetails,
      deleteTask,
      addTaskDocuments,
      removeTaskDocument,
      fetchTasks,
      getCaseTasks,
      getAssigneeTasks,
      findById,
    ],
  );
};
